import * as React from "react";
import { Callout } from "@octopusdeploy/design-system-components";
import {DataBaseComponent, DataBaseComponentState} from "components/DataBaseComponent";
import {HostedInstanceResource} from "client/resources/hostedInstanceResource";
import {HostedInstanceCorrectVersionRequest} from "client/resources/hostedInstanceCorrectVersionRequest";
import SaveDialogLayout from "components/DialogLayout/SaveDialogLayout";
import {Text} from "components/form";
import repository from "client/repository";

interface CorrectInstanceVersionDialogProps {
    instance: HostedInstanceResource;
    onSaved: (updatedInstance: HostedInstanceResource) => void;
}

interface CorrectInstanceVersionDialogState extends DataBaseComponentState {
    version: string;
    isVersionFromInstanceAvailable: boolean | null;
}

export class CorrectInstanceVersionDialog extends DataBaseComponent<CorrectInstanceVersionDialogProps, CorrectInstanceVersionDialogState> {
    constructor(props: CorrectInstanceVersionDialogProps) {
        super(props);
        this.state = {
            version: props.instance.Version || "",
            isVersionFromInstanceAvailable: null
        };
    }

    componentDidMount() {
        return this.doBusyTask(async () => {
            try {
                const response = await repository.HostedInstances.getLiveVersionFromInstance(this.props.instance.Id);
                if (response.Version) {
                    this.setState({ version: response.Version, isVersionFromInstanceAvailable: true });
                } else {
                    this.setState({ isVersionFromInstanceAvailable: false });
                }
            } catch {
                this.setState({ isVersionFromInstanceAvailable: false });
            }
        });
    }

    render() {
        return <SaveDialogLayout title={"Correct Instance Version"}
                                 errors={this.state.errors}
                                 busy={this.state.busy}
                                 onSaveClick={() => this.correctVersion()}>

            <p>This dialog can be used to fix the Instance version recorded in Cloud Portal if it gets out of sync with the Instance's actual version. 
                It <strong>does not</strong> Upgrade the instance. If you're trying to upgrade the instance, please refer to our hosted-docs for instructions.</p>
            <Callout type="warning" title="Warning">
                <p>Manually setting the version of an Instance is a <strong>dangerous operation</strong> that can break the instance if you choose the wrong version!</p>
                <p>
                    Please make sure that you:<br />
                    1. <strong>Pair with someone</strong> on the team to make this change<br />
                    2. <strong>Verify the chosen version</strong> before setting it through the Instance&apos;s logs and/or local testing<br />
                    3. <strong>Notify Support and monitor</strong> the next reprovision of the Instance to make sure nothing breaks.
                </p>
            </Callout>

            {this.state.isVersionFromInstanceAvailable !== null && (
                this.state.isVersionFromInstanceAvailable
                    ? <Callout type="information" title="Version pre-populated from the Instance">
                        <p>The version below was retrieved from the running Instance. Verify it is correct before saving.</p>
                    </Callout>
                    : <Callout type="warning" title="Could not retrieve the version from the Instance">
                        <p>The Instance is unreachable or not running. The version below is Cloud Portal&apos;s current record. <strong>Verify it matches the actual running version before saving</strong>.</p>
                    </Callout>
            )}

            <Text label="Version"
                  hintText="Current Octopus Server version (e.g. 2024.1.0)"
                  value={this.state.version}
                  onChange={version => this.setState({version})}
                  autoFocus={true}
            />
        </SaveDialogLayout>;
    }

    private correctVersion() {
        return this.doBusyTask(async () => {
            const request: HostedInstanceCorrectVersionRequest = {Version: this.state.version};
            const instance = await repository.HostedInstances.correctVersion(this.props.instance, request);
            this.props.onSaved(instance);
            return true;
        });
    }
}
